import React, { useEffect } from 'react';
import styled from 'styled-components';
import ScheduleIcon from '@mui/icons-material/Schedule';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { useRestaurantStatus } from '../../hooks/useRestaurantStatus';
import dayjs from '../../../../utils/dayjs';
import {
  AlertContainer,
  AlertMessageContainer,
  AlertHeading,
  AlertSubHeading,
  AlertContentContainer,
} from '../alert';
import { typography } from '../../../../theme';
import { colors } from '../../../../theme/color';
import { useOrder } from '../../../../state/order/hooks/useOrder';
import { useOrderRestaurant } from '../../../../state/order/hooks/useOrderRestaurant';
import { restaurantOpeningHoursStatusTypes } from '../../types';
import { linkToRestaurantPage } from '../../../addressSelection/components/restaurant-list/helpers';
import LinkNoPrefetch from '../../../linkNoPrefetch/LinkNoPrefetch';

import type {
  OpeningHoursIntervalDto,
  OpeningHoursIntervalsDto,
} from '@kotipizzagroup/kotipizza-shops-api-client';

const Link = styled(LinkNoPrefetch)`
  color: ${colors.black};
  text-decoration: underline;
`;

const OpeningHourLink: React.FC = () => {
  const { restaurant } = useOrderRestaurant();

  if (!restaurant) return null;

  return <Link href={linkToRestaurantPage(restaurant.displayName)}>Aukioloajat</Link>;
};

export const RestaurantOpeningBanner: React.FC = () => {
  const { data, isLoading } = useRestaurantStatus();
  const order = useOrder();

  useEffect(() => {
    if (data?.status) scrollTo({ top: 0 });
  }, [data?.status]);

  if (isLoading || data === undefined) {
    return null;
  }

  const currentMethodOpeningHourIntervals =
    data?.openingHoursIntervals &&
    data.openingHoursIntervals[order.type as keyof OpeningHoursIntervalsDto];
  const currentMethodOpeningHours: OpeningHoursIntervalDto | null =
    currentMethodOpeningHourIntervals ? currentMethodOpeningHourIntervals[0] : null;

  switch (data.status) {
    case restaurantOpeningHoursStatusTypes.CLOSING_SOON:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <ScheduleIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola sulkee pian</AlertHeading>
              <AlertSubHeading>
                <span>Tilaathan klo</span>{' '}
                <typography.Text>
                  <strong>{dayjs(currentMethodOpeningHours?.end).format('HH.mm')}</strong>
                </typography.Text>{' '}
                <span>mennessä.</span>
              </AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.TEMPORARILY_NOT_DELIVERING:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <NightsStayIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola ei kuljeta juuri nyt</AlertHeading>
              <AlertSubHeading>Valitse toinen ravintola tai tee noutotilaus</AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.TEMPORARILY_NOT_ONLINE:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <NightsStayIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola ei vastaanota verkkokauppatilauksia juuri nyt</AlertHeading>
              <AlertSubHeading>Valitse toinen ravintola</AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.DELIVERY_CLOSED:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <NightsStayIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola ei kuljeta enää tänään</AlertHeading>
              <AlertSubHeading>
                <span>Voit tehdä noutotilauksen klo</span>{' '}
                <typography.Text>
                  <strong>
                    {dayjs(
                      data.openingHoursIntervals?.pickup &&
                        data.openingHoursIntervals.pickup[0]?.end
                    ).format('HH.mm')}
                  </strong>
                </typography.Text>{' '}
                <span>asti. </span>
                <OpeningHourLink />
                <span>.</span>
              </AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.PHYSICALLY_OPEN_WITHOUT_ONLINE_ORDERS:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <NightsStayIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola on sulkeutumassa</AlertHeading>
              <AlertSubHeading>
                <span>Ravintola sulkeutuu klo</span>{' '}
                {dayjs(
                  data.openingHoursIntervals?.restaurant &&
                    data.openingHoursIntervals?.restaurant[0]?.end
                ).format('HH.mm')}
                <span>. Emme ota enää tilauksia tälle illalle. Avaamme taas huomenna klo</span>{' '}
                <typography.Text>
                  <strong>
                    {/* the next day opening hour */}
                    {dayjs(
                      data.openingHoursIntervals?.restaurant &&
                        data.openingHoursIntervals.restaurant[1]?.start
                    ).format('HH.mm')}
                  </strong>
                </typography.Text>
                <span>. </span>
                <OpeningHourLink />
                <span>.</span>
              </AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.CLOSED:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <NightsStayIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>Ravintola on kiinni</AlertHeading>
              <AlertSubHeading>
                <span>Ravintola sulkeutui klo</span>{' '}
                {dayjs(
                  data.openingHoursIntervals?.restaurant &&
                    data.openingHoursIntervals.restaurant[0]?.end
                ).format('HH.mm')}
                <span>. Avaamme taas huomenna klo</span>{' '}
                <typography.Text>
                  <strong>
                    {/* the next day opening hour */}
                    {dayjs(
                      data.openingHoursIntervals?.restaurant &&
                        data.openingHoursIntervals.restaurant[1]?.start
                    ).format('HH.mm')}
                  </strong>
                </typography.Text>
                <span>. </span>
                <OpeningHourLink />
                <span>.</span>
              </AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    case restaurantOpeningHoursStatusTypes.OPENING_SOON:
      return (
        <AlertContainer data-testid="opening-hour-banner">
          <AlertContentContainer>
            <ScheduleIcon style={{ fontSize: 32 }} />
            <AlertMessageContainer>
              <AlertHeading>
                Ravintola aukeaa klo {dayjs(currentMethodOpeningHours?.start).format('HH.mm')}
              </AlertHeading>
              <AlertSubHeading>Voit tehdä ennakkotilauksen myöhemmälle ajalle.</AlertSubHeading>
            </AlertMessageContainer>
          </AlertContentContainer>
        </AlertContainer>
      );
    default:
      return null;
  }
};
